import React, { FC, MouseEventHandler } from 'react';

import cx from 'clsx';
import { Icon } from '@mdi/react';
import { mdiCheck, mdiClose, mdiHelp } from '@mdi/js';
import { useLocale } from 'lib';
import styles from './style.module.scss';

type Choice = boolean | 'unknown';

export type ContactSwitchProps = {
  checked: Choice;
  setChecked: (value: Choice) => void;
  /** Once a choice is made, show it as a label (click to flip) instead of the two-way switch: do they help / does the alarm creator need help. */
  labels?: 'helper' | 'alarmCreator';
};

// The alarm creator can also be unreachable ('unknown'): cross, question mark, tick.
const order: Choice[] = [false, 'unknown', true];

export const ContactSwitch: FC<ContactSwitchProps> = ({ checked, setChecked, labels }) => {

  const { t } = useLocale();

  const onClick = (value: Choice): MouseEventHandler<HTMLDivElement> => {

    return (e) => {

      e.stopPropagation();

      if (checked !== value) {

        setChecked?.(value);

      }

    };

  };

  const decided = typeof checked === 'boolean' || checked === 'unknown';

  const views = {
    alarmCreator: {
      true: { text: t('Alarm_Detail_Response.Contact.No_Help_Needed'), icon: mdiCheck, className: styles.checked },
      false: { text: t('Alarm_Detail_Response.Contact.Needs_Help'), icon: mdiClose, className: styles.notChecked },
      unknown: { text: t('Alarm_Detail_Response.Contact.Unknown'), icon: mdiHelp, className: styles.unknown },
    },
    helper: {
      true: { text: t('Alarm_Detail_Response.Contact.Helps'), icon: mdiCheck, className: styles.checked },
      false: { text: t('Alarm_Detail_Response.Contact.Helps_Not'), icon: mdiClose, className: styles.notChecked },
      unknown: { text: t('Alarm_Detail_Response.Contact.Unknown'), icon: mdiHelp, className: styles.unknown },
    },
  }[labels ?? 'helper'];

  if (labels && decided) {

    const view = views[`${checked}`];

    return (
      <div
        className={cx(styles.choice, view.className)}
        // Step through cross, question mark and tick
        onClick={onClick(order[(order.indexOf(checked) + 1) % order.length])}
      >
        <Icon path={view.icon} className={view.icon === mdiHelp ? styles.question : undefined}/>
        {view.text}
      </div>
    );

  }

  return (
    <div className={styles.contactSwitch}>
      <div
        className={cx(
          styles.icon,
          checked === false && styles.active,
        )}
        data-tip={views.false.text}
        onClick={onClick(false)}
      >
        <Icon path={mdiClose}/>
      </div>
      <div
        className={cx(
          styles.icon,
          checked === 'unknown' && styles.active,
        )}
        data-tip={views.unknown.text}
        onClick={onClick('unknown')}
      >
        <Icon path={mdiHelp} className={styles.question}/>
      </div>
      <div
        className={cx(
          styles.icon,
          checked === true && styles.active,
        )}
        data-tip={views.true.text}
        onClick={onClick(true)}
      >
        <Icon path={mdiCheck}/>
      </div>
      <div className={cx(
        styles.iconBackground,
        checked === true && styles.checked,
        checked === false && styles.notChecked,
        checked === 'unknown' && styles.unknown,
      )}/>
    </div>
  );

};
